import { useState, useEffect } from "react";

const COOKIE_KEY = "lf_cookie_consent";

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true,
    analytics: true,
    marketing: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_KEY);
    if (!consent) {
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem(COOKIE_KEY, JSON.stringify({ all: true, timestamp: Date.now() }));
    setVisible(false);
  };

  const handleReject = () => {
    localStorage.setItem(COOKIE_KEY, JSON.stringify({ all: false, essential: true, timestamp: Date.now() }));
    setVisible(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem(COOKIE_KEY, JSON.stringify({ ...preferences, timestamp: Date.now() }));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-banner"
      className="fixed bottom-0 left-0 right-0 z-[60] p-4 md:p-6"
      style={{ pointerEvents: "none" }}
    >
      <div
        className="max-w-2xl mx-auto bg-white rounded-2xl shadow-[0_8px_40px_rgba(0,0,0,0.08)] border border-[#1A1B1A]/[0.06] p-5 md:p-6"
        style={{
          pointerEvents: "auto",
          animation: "fadeInUp 0.5s ease-out forwards",
        }}
      >
        {!showPreferences ? (
          <>
            <p className="text-sm text-[#4A4B4A] leading-relaxed mb-5">
              We use cookies to improve site performance, analyse traffic, and support a better experience. You can accept all cookies, reject non-essential cookies, or manage your preferences.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <button
                data-testid="cookie-accept-btn"
                onClick={handleAccept}
                className="bg-[#205B5A] text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-[#174544] transition-colors duration-200"
              >
                Accept
              </button>
              <button
                data-testid="cookie-reject-btn"
                onClick={handleReject}
                className="border border-[#1A1B1A]/15 text-[#1A1B1A] px-5 py-2.5 rounded-full text-sm font-medium hover:border-[#1A1B1A]/30 transition-colors duration-200"
              >
                Reject
              </button>
              <button
                data-testid="cookie-manage-btn"
                onClick={() => setShowPreferences(true)}
                className="text-sm font-medium text-[#205B5A] hover:text-[#174544] transition-colors duration-200 px-3 py-2.5"
              >
                Manage preferences
              </button>
            </div>
          </>
        ) : (
          <>
            <p
              className="text-base font-medium text-[#1A1B1A] mb-4"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              Cookie preferences
            </p>
            <div className="space-y-3 mb-5">
              {[
                { key: "essential", label: "Essential", desc: "Required for the site to function", locked: true },
                { key: "analytics", label: "Analytics", desc: "Helps us understand how visitors use the site" },
                { key: "marketing", label: "Marketing", desc: "Used to deliver relevant content and ads" },
              ].map((item) => (
                <div key={item.key} className="flex items-center justify-between py-2">
                  <div>
                    <p className="text-sm font-medium text-[#1A1B1A]">{item.label}</p>
                    <p className="text-xs text-[#757675]">{item.desc}</p>
                  </div>
                  <button
                    data-testid={`cookie-toggle-${item.key}`}
                    onClick={() => {
                      if (!item.locked) {
                        setPreferences((p) => ({ ...p, [item.key]: !p[item.key] }));
                      }
                    }}
                    className={`w-10 h-5.5 rounded-full relative transition-colors duration-200 flex-shrink-0 ${
                      preferences[item.key] ? "bg-[#205B5A]" : "bg-[#1A1B1A]/15"
                    } ${item.locked ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
                    style={{ width: 40, height: 22 }}
                    disabled={item.locked}
                  >
                    <span
                      className={`absolute top-[2px] w-[18px] h-[18px] rounded-full bg-white shadow-sm transition-transform duration-200 ${
                        preferences[item.key] ? "translate-x-[20px]" : "translate-x-[2px]"
                      }`}
                    />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-3">
              <button
                data-testid="cookie-save-preferences-btn"
                onClick={handleSavePreferences}
                className="bg-[#205B5A] text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-[#174544] transition-colors duration-200"
              >
                Save preferences
              </button>
              <button
                data-testid="cookie-back-btn"
                onClick={() => setShowPreferences(false)}
                className="text-sm font-medium text-[#4A4B4A] hover:text-[#1A1B1A] transition-colors duration-200 px-3 py-2.5"
              >
                Back
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CookieBanner;
